/**
 * The console's staff sign-in client — task 31, closing the gap task 27
 * flagged: services/auth has a fully-built, reviewed staff sign-in (email
 * plus a one-time code, restricted to the configured staff domain), but
 * nothing under `apps/console` ever called it. This is that call site.
 *
 * Same OTP primitive apps/client/src/lib/auth.ts uses for the *client*
 * sign-in path (services/auth's one otp.ts — see
 * .superpowers/sdd/2026-08-05-communicator-phase-1/task-9-report.md for the
 * frozen shapes below, not guessed) — this is not a second implementation of
 * email+code, it is the staff-domain-restricted pair of endpoints against
 * that same primitive:
 *
 *   POST /auth/signin/staff        { email }        -> 200 { ok: true } (or 403/429)
 *   POST /auth/signin/staff/verify { email, code }   -> 200 { ok, staff } (sets the
 *                                                         session + refresh cookies), or 401
 *   POST /auth/signout                                -> 200 { ok: true } always (task 31 —
 *                                                         revokes the session, clears both cookies)
 *
 * Session persistence for staff is cookie-based (HttpOnly, browser-managed,
 * services/auth/src/app.ts), unlike the client app's bearer-token-in-
 * localStorage flow — there is no token for this module to store.
 * `credentials: 'include'` on the verify/sign-out calls is what lets the
 * browser send and receive those cookies at all.
 *
 * Entra staff sign-in (communicatorv2.3.md §5's end state — plain SSO against
 * the operating MSP's own tenant) is now built, and replaces only the FRONT
 * of sign-in. It is not another method this module implements: the browser
 * simply navigates to services/auth's own start route, comes back to its
 * callback with the same session cookies already set, and the console
 * finishes identically to the code path (`identity.whoAmI()` +
 * `store.setAuthStatus('signed-in')`, see src/pages/SignInPage.tsx). Two
 * additions serve it:
 *
 *   GET /auth/methods            -> 200 { emailOtp: boolean, entra: boolean }
 *   GET /auth/entra/start?returnTo&errorTo -> 302 to the identity provider
 *
 * Which methods are offered is read from `/auth/methods`, never hardcoded —
 * a deployment with no Entra block configured shows no Microsoft button, and
 * email plus a one-time code always works alongside it as the fallback.
 *
 * The start route is reached by a full-page navigation, deliberately: that
 * is the system browser (v2.3 §4 — "the system browser or the Windows
 * broker, never an embedded webview"). Nothing here opens a webview, an
 * iframe or a popup, and nothing here should grow one.
 */
import { loadRuntimeConfig } from '../runtime-config'

export interface StaffSignInResult {
  id: string
  name: string
  role: 'dispatch' | 'tech'
  admin: boolean
}

/** Which staff sign-in methods this deployment offers, per `GET /auth/methods`. */
export interface StaffSignInMethods {
  emailOtp: boolean
  entra: boolean
}

/**
 * What a caller falls back to when `/auth/methods` cannot be read at all:
 * email plus a one-time code only. Never guesses that Entra is available —
 * offering a button that leads nowhere is worse than not offering it.
 */
export const FALLBACK_SIGN_IN_METHODS: StaffSignInMethods = { emailOtp: true, entra: false }

/** The query parameter services/auth reports a refused Entra sign-in in. */
export const ENTRA_ERROR_PARAM = 'entraError'

/** Thrown by requestStaffCode/verifyStaffCode on a non-2xx response. */
export class StaffAuthError extends Error {
  readonly status: number
  readonly reason?: string
  constructor(status: number, message: string, reason?: string) {
    super(message)
    this.name = 'StaffAuthError'
    this.status = status
    this.reason = reason
  }
}

interface StaffAuthClientDeps {
  /** Defaults to the global `fetch`. Override in tests. */
  fetchImpl?: typeof fetch
  /**
   * Defaults to reading /runtime-config.json via loadRuntimeConfig() — the
   * same apiBase src/lib/ops.ts and src/lib/identity.ts use. Auth and ops
   * are reached through the same public origin (deploy/Caddyfile.snippet:
   * `/auth/*` -> services/auth, `/ops/*` -> services/ops, both on the one
   * public hostname), so no separate "auth base" config exists.
   */
  baseUrl?: string | (() => string | Promise<string>)
}

async function readJsonBody(res: Response): Promise<unknown> {
  try {
    return await res.json()
  } catch {
    return null
  }
}

/**
 * Builds the staff auth client. `apps/console` uses the default singleton
 * `staffAuth` below; this factory exists so `SignInPage` and tests can inject
 * a fake and skip the network entirely.
 */
export function createStaffAuthClient(deps: StaffAuthClientDeps = {}) {
  const doFetch: typeof fetch = deps.fetchImpl ?? ((...args) => fetch(...args))

  async function resolveBase(): Promise<string> {
    if (deps.baseUrl !== undefined) {
      return typeof deps.baseUrl === 'function' ? deps.baseUrl() : deps.baseUrl
    }
    return (await loadRuntimeConfig()).apiBase
  }

  /**
   * Always resolves on a 2xx. Throws StaffAuthError — carrying the server's
   * own message verbatim — on an out-of-domain address (403, named plainly:
   * "not eligible for staff sign-in — use your @<domain> address") or on
   * rate limiting (429), so the sign-in screen can show exactly why, rather
   * than failing mysteriously.
   */
  async function requestStaffCode(email: string): Promise<void> {
    const base = await resolveBase()
    const res = await doFetch(`${base}/auth/signin/staff`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ email }),
    })
    if (!res.ok) {
      const body = (await readJsonBody(res)) as { error?: string } | null
      throw new StaffAuthError(res.status, body?.error ?? `Request failed with status ${res.status}`)
    }
  }

  /**
   * On success, the session + refresh cookies are already set by the
   * response (credentials: 'include') — this returns only the
   * `{ id, name, role, admin }` the verify endpoint itself carries
   * (task-9-report.md). It does not carry `email`/`covering`; callers that
   * want the full record call `identity.whoAmI()` once more afterward (see
   * src/pages/SignInPage.tsx) now that the cookie the identity read needs is
   * in place.
   */
  async function verifyStaffCode(email: string, code: string): Promise<StaffSignInResult> {
    const base = await resolveBase()
    const res = await doFetch(`${base}/auth/signin/staff/verify`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ email, code }),
    })
    const body = await readJsonBody(res)
    if (!res.ok) {
      const errBody = body as { error?: string; reason?: string } | null
      throw new StaffAuthError(res.status, errBody?.error ?? `Request failed with status ${res.status}`, errBody?.reason)
    }
    return (body as { staff: StaffSignInResult }).staff
  }

  /**
   * Always resolves, never throws — the cookies are HttpOnly, so this
   * network call is the only way "Log out" can mean anything at all, but a
   * caller that is already signed out (or offline) should still land back on
   * an honest signed-out state rather than getting stuck. See
   * src/components/shell/InitialsMenu.tsx's `logOut`.
   */
  async function signOut(): Promise<void> {
    const base = await resolveBase()
    try {
      await doFetch(`${base}/auth/signout`, { method: 'POST', credentials: 'include' })
    } catch {
      // Network failure: local state (store.me, authStatus) still clears at
      // the call site regardless — an honest "signed out" locally beats
      // silently pretending a failed request quietly succeeded server-side.
    }
  }

  /**
   * Reads which methods this deployment offers. Never throws: an
   * unreachable auth service resolves to the email-plus-code fallback, so
   * the screen still renders something usable rather than an error page.
   */
  async function signInMethods(): Promise<StaffSignInMethods> {
    try {
      const base = await resolveBase()
      const res = await doFetch(`${base}/auth/methods`)
      if (!res.ok) return FALLBACK_SIGN_IN_METHODS
      const body = (await readJsonBody(res)) as Partial<StaffSignInMethods> | null
      return {
        emailOtp: body?.emailOtp !== false,
        entra: body?.entra === true,
      }
    } catch {
      return FALLBACK_SIGN_IN_METHODS
    }
  }

  /**
   * The URL that starts an Entra sign-in. Callers navigate the whole page to
   * it — see this module's header on why that, and only that.
   *
   * `returnTo`/`errorTo` are the console's own routes: services/auth has no
   * business knowing them, and refuses anything that is not a path on this
   * deployment, so an open redirect is not reachable from here either.
   */
  async function entraStartUrl(returnTo: string, errorTo: string): Promise<string> {
    const base = await resolveBase()
    const query = new URLSearchParams({ returnTo, errorTo })
    return `${base}/auth/entra/start?${query.toString()}`
  }

  return { requestStaffCode, verifyStaffCode, signOut, signInMethods, entraStartUrl }
}

/** The singleton every real caller (SignInPage, InitialsMenu) uses — same pattern as `ops`/`identity`. */
export const staffAuth = createStaffAuthClient()
